/**
 * PIN FLYER — the travelling location pin.
 *
 * Mounted once, high in the always-mounted App shell. It listens on the flight
 * bus, so it survives the /marketplace → /marketplace/search route change that
 * unmounts the search bar it launches from.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS rAF AND NOT CSS offset-path
 *
 * The mock (design-explorations/pink-bead/dashed-icon.html) could fly a pure
 * CSS offset-path because it knew where it was going: the map anchor was a
 * hardcoded (756,336) on a fixed plane.
 *
 * Reality doesn't offer that. The pin's target is the map's search anchor,
 * whose screen position isn't known until Google Maps has loaded its JS,
 * mounted, and finished fitBounds() — which lands somewhere around 700-900ms.
 * The flight has to launch at 300ms, because it launches from a search bar
 * that is being towed off screen and won't exist much longer.
 *
 * So the target genuinely is not knowable at launch. We fly anyway, toward a
 * provisional target, and steer onto the real anchor once it appears:
 *
 *     position = routePoint(routeProgress) + w * (realTarget - provisionalTarget)
 *
 * The correction is ADDITIVE, and — this is the part I got wrong twice before
 * simulating it — the homing runs on its OWN clock, started at the moment of
 * discovery, NOT on the route's timeline:
 *
 *     w = smoothstep((now - discoveredAt) / homingMs)
 *     homingMs = max(MIN_HOMING_MS, routeTimeRemainingAtDiscovery)
 *
 * Anchoring the ramp to a fixed fraction of the route instead (the obvious
 * design) means a late discovery snaps the correction straight to its
 * mid-ramp weight — a measured 188px teleport in a single frame. Starting the
 * ramp at discovery makes w provably 0 at that instant, so it cannot jump no
 * matter when the map finally settles; and spreading it over the remaining
 * route time keeps the common (early) case gentle.
 *
 * If the anchor turns up so late that homing outlives the route, the pin simply
 * holds at the route's end and sweeps on. Landing LATE is safe — the tow
 * invariant only forbids landing while the pages are still moving.
 *
 * If the anchor never appears at all (no results, no coords, map failed to
 * load), w stays 0: the pin completes its route to the provisional point and
 * fades. No crash, no pin stranded pointing at nothing.
 *
 * Simulated across discovery times 0-2000ms: exact landing every time, peak
 * 29px/frame, graceful when absent. See scratchpad homing4.mjs.
 *
 * Route geometry is sampled with SVGGeometryElement.getPointAtLength() rather
 * than a hand-rolled bezier evaluator: it's built into the browser, it's exact,
 * and it handles the cubics and the polyline (turn-by-turn) identically.
 * ---------------------------------------------------------------------------
 */
import { Box } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  buildRoutePath,
  FLIGHT_INK_DEEP,
  FLIGHT_INK_MAIN,
  FLIGHT_TIMING,
  type FlightRequest,
  onPinFlight,
  type Point,
  prefersReducedMotion,
} from './pin-flight-contract';

/**
 * The map's search anchor tags itself with this so we can find it without
 *  threading a ref across a route change. See results-map.tsx.
 */
export const ANCHOR_ATTR = 'data-pin-flight-anchor';

/**
 * The anchor is a PIN, and a pin's tip — not its centre — is the thing that sits
 * on the coordinate. The MUI LocationOn glyph's tip is ~92% down its box, the
 * same factor TIP_ANCHOR uses in results-map.tsx.
 *
 * Homing to the rect's CENTRE instead parks the flown pin's tip half a pin above
 * the target, which is exactly the "not going exactly to where the pin is" you
 * can see on screen even when the arithmetic claims a small number.
 */
const PIN_TIP_FACTOR = 0.92;

/**
 * Floor on the steering window, so a late anchor still gets a smooth sweep
 *  rather than a snap. Tuned by simulation: below ~280ms the worst case starts
 *  moving the pin more than its own body-length per frame.
 */
const MIN_HOMING_MS = 280;

/**
 * How long we keep flying past the route's end waiting for a slow map before
 *  giving up and landing on the provisional point.
 */
const GRACE_MS = 400;

/**
 * Frames of overlap between the flown pin vanishing and the map's permanent pin
 *  appearing. Two identical pins on one pixel read as one; a gap reads as a
 *  blink.
 */
const HANDOFF_OVERLAP_MS = 90;

/**
 * Smooth, zero-derivative at both ends — no visible kink when steering starts.
 * @param x
 */
function smoothstep(x: number): number {
  const t = Math.min(1, Math.max(0, x));
  return t * t * (3 - 2 * t);
}

/**
 * Where we aim before the real anchor exists: the middle of the map column,
 *  which on this layout is the right ~40% of the viewport. Only ever a
 *  placeholder — homing corrects whatever we get wrong here.
 */
function provisionalTarget(): Point {
  return { x: window.innerWidth * 0.78, y: window.innerHeight * 0.5 };
}

/**
 * The anchor pin's TIP in viewport coords — the point the flown pin's own tip
 *  must come to rest on, so the two pins register exactly and the handoff is
 *  invisible.
 */
function findAnchor(): Point | null {
  const el = document.querySelector(`[${ANCHOR_ATTR}]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  // Skip a zero-box anchor: the overlay exists but Google hasn't positioned it
  // yet, and (0,0) would drag the pin to the corner.
  if (r.width === 0 && r.height === 0) return null;
  return { x: r.left + r.width / 2, y: r.top + r.height * PIN_TIP_FACTOR };
}

interface Flight extends FlightRequest {
  path: string;
  provisional: Point;
}

export function PinFlyer() {
  // The plum shared with the map's search-anchor pin (contract). The trail takes
  // the lighter shade, the pin body the deeper one.
  const ink = FLIGHT_INK_MAIN;
  const inkDeep = FLIGHT_INK_DEEP;

  const [flight, setFlight] = useState<Flight | null>(null);
  const [landed, setLanded] = useState(false);

  const pinRef = useRef<HTMLDivElement | null>(null);
  const trailRef = useRef<SVGPathElement | null>(null);
  const geomRef = useRef<SVGPathElement | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(
    () =>
      onPinFlight((req) => {
        if (prefersReducedMotion()) return;
        const provisional = provisionalTarget();
        setLanded(false);
        setFlight({
          ...req,
          provisional,
          path: buildRoutePath(req.route, req.from, provisional),
        });
      }),
    [],
  );

  const run = useCallback((f: Flight) => {
    const geom = geomRef.current;
    const pin = pinRef.current;
    if (!geom || !pin) return;

    const total = geom.getTotalLength();
    const start = performance.now();
    // Park the pin on the launch point and reveal it in the same breath. Without
    // this it renders at the container's origin (0,0) the moment the flight is
    // requested and then snaps ~900px to the start of its route on the first
    // frame — a jump measured in the browser, invisible to the type checker.
    pin.style.transform = `translate3d(${f.from.x}px, ${f.from.y}px, 0)`;
    pin.style.opacity = '1';
    const routeMs = FLIGHT_TIMING.TOUCHDOWN - FLIGHT_TIMING.LAUNCH;
    const trail: Point[] = [];

    let anchor: Point | null = null;
    let discoveredAt = 0;
    let homingMs = MIN_HOMING_MS;

    const frame = (now: number) => {
      const elapsed = now - start;
      const routeT = Math.min(1, elapsed / routeMs);

      // Re-read the anchor EVERY frame, not just on first sight. Discovery is
      // not the end of the story: fitBounds/panTo are still animating the camera
      // afterwards, so the anchor keeps moving for a few hundred ms. Caching the
      // first position measured a pin that landed 426px from where the anchor
      // actually came to rest — confidently, and on nothing.
      const live = findAnchor();
      if (live) {
        if (!anchor) {
          discoveredAt = elapsed;
          // Spread the correction over whatever route time is left, so an early
          // discovery (the common case) barely registers as steering at all.
          homingMs = Math.max(MIN_HOMING_MS, routeMs - elapsed);
        }
        anchor = live;
      }

      const p = geom.getPointAtLength(routeT * total);
      const homingProgress = anchor ? (elapsed - discoveredAt) / homingMs : 0;
      const w = anchor ? smoothstep(homingProgress) : 0;
      const x = p.x + (anchor ? (anchor.x - f.provisional.x) * w : 0);
      const y = p.y + (anchor ? (anchor.y - f.provisional.y) * w : 0);

      pin.style.transform = `translate3d(${x}px, ${y}px, 0)`;

      // The trail is the pin's actual history, not a precomputed path. That
      // makes it exact by construction — it cannot drift from the flown route
      // the way a separately-authored path could, and it needs no reveal mask.
      trail.push({ x, y });
      if (trailRef.current && trail.length > 1) {
        trailRef.current.setAttribute(
          'd',
          `M${trail[0].x.toFixed(1)},${trail[0].y.toFixed(1)}${trail
            .slice(1)
            .map((q) => `L${q.x.toFixed(1)},${q.y.toFixed(1)}`)
            .join('')}`,
        );
      }

      // Done when the route has run AND we've either finished steering onto the
      // anchor, or waited out the grace window for a map that never arrived.
      const settled = anchor
        ? homingProgress >= 1
        : elapsed >= routeMs + GRACE_MS;
      if (routeT < 1 || !settled) {
        rafRef.current = requestAnimationFrame(frame);
        return;
      }

      setLanded(true);

      // Landing is not the end: the map's camera can still be easing after
      // touchdown, and every pixel it moves is a pixel the (now static) pin gets
      // left behind by. Measured ~7px of horizontal drift across repeated
      // searches — small, but it's the difference between the pin sitting ON the
      // anchor and merely near it. So keep it glued until teardown.
      const glue = () => {
        const anchorNow = findAnchor();
        if (anchorNow) {
          pin.style.transform = `translate3d(${anchorNow.x}px, ${anchorNow.y}px, 0)`;
        }
        rafRef.current = requestAnimationFrame(glue);
      };
      rafRef.current = requestAnimationFrame(glue);
    };
    rafRef.current = requestAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (!flight) return undefined;
    const launchIn = Math.max(0, FLIGHT_TIMING.LAUNCH - FLIGHT_TIMING.EMERGE);
    const launch = window.setTimeout(() => run(flight), launchIn);
    // Hand off AT touchdown. The map's anchor pin appears on that same beat and
    // plays the landing itself — it's the pin that stays, so it should be the
    // one you watch arrive. The flown pin is only a stand-in for the trip.
    //
    // Overlap by a couple of frames rather than switching on the exact
    // millisecond: two identical pins stacked on the same pixel are invisible,
    // whereas being even one frame apart leaves a hole. An earlier cut had the
    // landing play on the flown pin and the anchor appear afterwards, which left
    // a measurable window with no pin on screen at all.
    const done = window.setTimeout(
      () => setFlight(null),
      FLIGHT_TIMING.TOUCHDOWN + HANDOFF_OVERLAP_MS,
    );
    return () => {
      window.clearTimeout(launch);
      window.clearTimeout(done);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [flight, run]);

  if (!flight) return null;

  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal + 5,
        pointerEvents: 'none',
      }}
    >
      {/* geometry probe — never painted, only sampled */}
      <svg width={0} height={0} style={{ position: 'absolute' }} aria-hidden>
        <path ref={geomRef} d={flight.path} fill="none" />
      </svg>

      {/* the dashed route, emitted behind the pin as it flies */}
      <Box
        component="svg"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          overflow: 'visible',
        }}
        aria-hidden
      >
        <Box
          component="path"
          ref={trailRef}
          sx={{
            fill: 'none',
            stroke: ink,
            strokeWidth: 2.5,
            strokeLinecap: 'round',
            strokeDasharray: '7 6',
            opacity: landed ? 0.16 : 0.9,
            transition: 'opacity 420ms ease',
          }}
        />
      </Box>

      {/* The pin in transit. translate3d is written per frame by run().
          It carries no landing beat: the map's permanent anchor pin plays that,
          because it's the one that stays. This is purely the trip. */}
      <Box
        ref={pinRef}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: 0,
          willChange: 'transform',
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            ml: '-13px',
            mt: '-24px',
            // tip-anchored, exactly as the map's own pins are (results-map.tsx)
            transformOrigin: '50% 92%',
          }}
        >
          <svg viewBox="0 0 24 24" width={26} height={26} aria-hidden>
            <path
              d="M12,1.6 C7.9,1.6 4.6,4.9 4.6,9 C4.6,14.6 12,22.4 12,22.4 C12,22.4 19.4,14.6 19.4,9 C19.4,4.9 16.1,1.6 12,1.6 Z"
              fill={inkDeep}
              stroke="#fff"
              strokeWidth={1.4}
            />
            <circle cx={12} cy={8.9} r={2.6} fill="#fff" />
          </svg>
        </Box>
      </Box>
    </Box>
  );
}
